import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'
import Avatar from '@mui/material/Avatar'
import LaunchIcon from '@mui/icons-material/Launch'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import { BootstrapDialog, BootstrapDialogTitle } from '../shared/BootstrapDialog'
import ResourceDescription from '../shared/ResourceDescription'
import useFavourite from '../../hooks/useFavourite'
import { copyResourceLink } from './copyResourceLink'
import { openExternal } from '../../utils/safeUrl'

// Shared public detail modal for the media types that list sub-resources
// (Course lessons, Podcast talks, Video playlist entries). Everything that
// differs per type arrives through `config`; see the thin wrappers
// CourseModal / PodcastModal / VideoModal.
const MediaModal = ({ config, ...props }) => {
    const { Icon, style, favouriteType, resourcePath, authorsLabel, subKey, subHeading, rowKeyPrefix } = config
    const subRows = props[subKey]
    const user = useSelector(state => state.user)
    const dispatch = useDispatch()

    const [open, setOpen] = useState(props.initialOpen || false)
    const { favourited, toggleFavourite } = useFavourite(props, favouriteType)

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
        if (props.onExternalClose) props.onExternalClose()
    }

    const openResource = (link) => {
        openExternal(link)
        return false;
    }

    const handleCopyLink = async () => {
        await copyResourceLink({ dispatch, resourcePath, resourceId: props.id })
    }

    return (
        <div>
            {!props.initialOpen && <Button {...style.viewButton.props} sx={style.viewButton.style} onClick={()=>handleClickOpen()}>
                More Info
            </Button>}
            <BootstrapDialog
                onClose={()=>handleClose()}
                aria-labelledby="customized-dialog-title"
                open={open}
                fullWidth
                {...style.modal}
            >
                <BootstrapDialogTitle id="customized-dialog-title" onClose={()=>handleClose()}>
                    <Box sx={style.headingContainer}>
                        <Icon sx={style.icon} />
                        {props.title}
                    </Box>
                </BootstrapDialogTitle>
                <DialogContent dividers>
                    <Box sx={style.content}>
                        <Box sx={style.dateContainer}>
                            <Box sx={style.dates}>
                                <Typography {...style.heading}>
                                    Posted:&nbsp;
                                </Typography>
                                <Typography {...style.text}>
                                    {props.updated.split('T')[0]}
                                </Typography>
                            </Box>
                            <Box sx={style.dates}>
                                <Typography {...style.heading}>
                                    Published:&nbsp;
                                </Typography>
                                <Typography {...style.text}>
                                    {props.pub_date}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Box sx={style.ratingContainer}>
                                {user && !user.anon && <IconButton onClick={toggleFavourite}>
                                    {favourited? <BookmarkIcon sx={style.favColour} /> : <BookmarkBorderIcon sx={style.favColour} />}
                                </IconButton>}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={style.mobileContainer}>
                        <Box sx={style.mobileRating}>
                            {user && !user.anon && <IconButton onClick={toggleFavourite}>
                                {favourited? <BookmarkIcon sx={style.favColour} /> : <BookmarkBorderIcon sx={style.favColour} />}
                            </IconButton>}
                        </Box>
                        <Box sx={style.mobileDate}>
                            <Typography gutterBottom {...style.heading}>
                                Posted:&nbsp;
                            </Typography>
                            <Typography gutterBottom {...style.text}>
                                {props.updated.split('T')[0]}
                            </Typography>
                        </Box>
                        <Box sx={style.mobileDate}>
                            <Typography gutterBottom {...style.heading}>
                                Published:&nbsp;
                            </Typography>
                            <Typography gutterBottom {...style.text}>
                                {props.pub_date}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={style.additionalInfo}>
                        <Typography {...style.text} gutterBottom>
                            <strong>{authorsLabel}</strong> {props.authors}
                        </Typography>
                    </Box>
                    {(subRows && subRows.length > 0) && <><Box sx={style.additionalInfo}>
                        <Typography {...style.heading} gutterBottom>
                            {subHeading}
                        </Typography>
                        <List dense={true}>
                            {subRows.map(row => (
                                <ListItem
                                key={`${rowKeyPrefix}${row.id}`}
                                secondaryAction={
                                    <IconButton edge="end" aria-label="view" onClick={() => openResource(row.link)}>
                                        <LaunchIcon />
                                    </IconButton>
                                }
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={style.avatar}>
                                            <Icon sx={style.avatarIcon} />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={row.title}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box></>}
                    <ResourceDescription
                        description={props.description}
                        containerSx={style.additionalInfo}
                        typographyProps={{ ...style.text, gutterBottom: true }}
                    />
                    {((props.tags && props.tags.length > 0) || (props.content_tags && props.content_tags.length > 0) || (props.medical_specialty_tags && props.medical_specialty_tags.length > 0)) &&
                    <><Box sx={style.additionalInfo}>
                        <Typography {...style.heading} gutterBottom>
                            Key Words
                        </Typography>
                    </Box>
                    <Box>
                        {props.tags && props.tags.map(tag => <Chip key={tag} label={tag} sx={style.tags}/>)}
                        {props.content_tags && props.content_tags.map(tag => <Chip key={tag} label={tag} sx={style.tags}/>)}
                        {props.medical_specialty_tags && props.medical_specialty_tags.map(tag => <Chip key={tag} label={tag} sx={style.tags}/>)}
                    </Box></>}

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCopyLink}>
                        Copy Link&nbsp;<ContentCopyIcon />
                    </Button>
                    <Button onClick={() => openResource(props.link)}>
                        View Resource&nbsp;<LaunchIcon />
                    </Button>
                    <Button autoFocus onClick={()=>handleClose()}>
                        Close
                    </Button>
                </DialogActions>
            </BootstrapDialog>
        </div>
    )
}

export default MediaModal
